/*
 * Purpose: Passwordless auth config — provider, brand, endpoints (Vite)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 94
 */
import { apiUrl } from '@/lib/apiBase';
import { COMPANY_INFO } from '@/lib/companyInfo';
export type AuthProvider = 'mock' | 'same-origin-api' | 'external-api' | 'api-proxy';

export type AuthMethod = 'magic_link' | 'email_link' | 'email_otp' | 'passkey' | 'github_oauth';

const viteEnv =
  typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env : ({} as ImportMetaEnv);

const provider = (viteEnv.VITE_AUTH_PROVIDER || 'mock') as AuthProvider;

export const AUTH_CONFIG = {
  provider,
  mock: provider === 'mock' || viteEnv.VITE_AUTH_MOCK === '1',
  brand: viteEnv.VITE_AUTH_BRAND || 'Autoblocks',
  postLoginPath: '/dashboard',
  signInPath: '/auth/sign-in',
  lostKeyPath: '/auth/lost-key',
  apiBase: (viteEnv.VITE_AUTH_API_BASE || '').replace(/\/$/, ''),
  enabledMethods: [
    'magic_link',
    'email_otp',
    'passkey',
    'github_oauth',
  ] as AuthMethod[],
  endpoints: {
    session: '/api/auth/session',
    magicLink: '/api/auth/magic-link',
    magicLinkVerify: '/api/auth/magic-link/verify',
    emailLink: '/api/auth/email-link',
    emailOtpSend: '/api/auth/email-otp/send',
    emailOtpVerify: '/api/auth/email-otp/verify',
    passkeyLoginOptions: '/api/auth/passkey/login/options',
    passkeyLoginVerify: '/api/auth/passkey/login/verify',
    passkeyRegisterOptions: '/api/auth/passkey/register/options',
    passkeyRegisterVerify: '/api/auth/passkey/register/verify',
    logout: '/api/auth/logout',
    tenant: '/api/auth/tenant',
    callback: '/api/auth/callback',
    githubStart: '/api/auth/github/start',
    githubCallback: '/api/auth/github/callback',
  },
  userState: {
    get: '/api/user/state',
    patch: '/api/user/state',
  },
} as const;

export function authUrl(path: string): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    const isAuthOrUserState = path.startsWith('/api/auth/') || path.startsWith('/api/user/');
    // Always use canonical API for auth/session endpoints in deployed environments.
    if (!isLocal && isAuthOrUserState) {
      return `${COMPANY_INFO.officialApiUrl}${path.startsWith('/') ? path : `/${path}`}`;
    }
  }
  return apiUrl(path);
}
